import React, { useCallback, useEffect, useMemo, useState, createContext, useContext } from 'react';
import { getCompanies } from '../utils/companyManagement';
import { supabaseClient, isSupabaseConfigured, generateSecureId } from '../lib/supabase';
// Notification sound for new orders on dashboard
let notificationAudioCtx: AudioContext | null = null;
function playNewOrderSound() {
  try {
    if (!notificationAudioCtx || notificationAudioCtx.state === 'closed') {
      notificationAudioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    const ctx = notificationAudioCtx;
    if (ctx.state === 'suspended') {
      ctx.resume();
    }
    const notes = [{
      freq: 587.33,
      start: 0,
      end: 0.15
    }, {
      freq: 783.99,
      start: 0.12,
      end: 0.27
    }, {
      freq: 1046.5,
      start: 0.24,
      end: 0.6
    }];
    notes.forEach(({
      freq,
      start,
      end
    }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, ctx.currentTime + start);
      gain.gain.setValueAtTime(0.25, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + end);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + end);
    });
  } catch (e) {
    // Audio not supported
  }
}
export interface Order {
  id: string;
  orderNo: string;
  orderDate: string;
  orderDateISO?: string;
  deliveryDate: string;
  type: string;
  name: string;
  company: string;
  branch: string;
  product: string;
  cashier: string;
  quantity: number;
  catalogType: string;
  subtotal: string;
  discount: string;
  subsidyAmount?: string;
  subsidyAppliedBy?: string;
  promotionId?: string;
  promotionDiscount?: string;
  total: string;
  status: 'Pendiente' | 'En Preparación' | 'Listo' | 'Entregado' | 'Cancelado';
  labelStatus: 'Pendiente' | 'Generado';
  taxExempt?: boolean;
  employeeId?: string;
  claimId?: string;
}
interface OrderContextType {
  orders: Order[];
  loading: boolean;
  refreshOrders: () => Promise<void>;
  addOrder: (order: Omit<Order, 'id' | 'orderNo' | 'orderDate' | 'status' | 'labelStatus'>) => Promise<string>;
  updateOrderStatus: (orderId: string, status: Order['status']) => Promise<void>;
  updateLabelStatus: (orderIds: string[], status: 'Generado') => Promise<void>;
  cancelOrder: (orderId: string) => Promise<void>;
  applySubsidy: (orderId: string, subsidyAmount: string, appliedBy: string) => Promise<void>;
  filteredOrders: (searchTerm: string, company: string, status?: string) => Order[];
}
const OrderContext = createContext<OrderContextType | undefined>(undefined);
// Separate context for stable action functions — consumers that only need
// addOrder (e.g. EmployeeOrderPage) won't re-render when orders change
interface OrderActionsContextType {
  addOrder: OrderContextType['addOrder'];
  updateOrderStatus: OrderContextType['updateOrderStatus'];
  updateLabelStatus: OrderContextType['updateLabelStatus'];
  cancelOrder: OrderContextType['cancelOrder'];
  applySubsidy: OrderContextType['applySubsidy'];
  refreshOrders: OrderContextType['refreshOrders'];
}
const OrderActionsContext = createContext<OrderActionsContextType | undefined>(undefined);
// Helper: Convertir fecha UTC a hora de República Dominicana (UTC-4)
function toRDTime(dateInput: string | Date): string {
  let date: Date;
  if (typeof dateInput === 'string') {
    let dateStr = dateInput.trim();
    if (!dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.match(/\-\d{2}:\d{2}$/)) {
      dateStr = dateStr + 'Z';
    }
    date = new Date(dateStr);
  } else {
    date = dateInput;
  }
  if (isNaN(date.getTime())) {
    return dateInput?.toString() || 'Fecha inválida';
  }
  let totalMinutes = date.getUTCHours() * 60 + date.getUTCMinutes() - 4 * 60;
  let dayOffset = 0;
  if (totalMinutes < 0) {
    totalMinutes += 24 * 60;
    dayOffset = -1;
  } else if (totalMinutes >= 24 * 60) {
    totalMinutes -= 24 * 60;
    dayOffset = 1;
  }
  const rdHour24 = Math.floor(totalMinutes / 60);
  const rdMinutes = totalMinutes % 60;
  const rdSeconds = date.getUTCSeconds();
  const tempDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + dayOffset));
  const day = String(tempDate.getUTCDate()).padStart(2, '0');
  const month = String(tempDate.getUTCMonth() + 1).padStart(2, '0');
  const year = tempDate.getUTCFullYear();
  const ampm = rdHour24 >= 12 ? 'p. m.' : 'a. m.';
  let displayHour = rdHour24 % 12;
  if (displayHour === 0) displayHour = 12;
  return day + '/' + month + '/' + year + ', ' + String(displayHour).padStart(2, '0') + ':' + String(rdMinutes).padStart(2, '0') + ':' + String(rdSeconds).padStart(2, '0') + ' ' + ampm;
}
function calculateSubsidyAmount(subtotal: number, subsidyConfig: string): number {
  if (subsidyConfig.includes('%')) {
    const percentage = parseFloat(subsidyConfig.replace('%', ''));
    return subtotal * (percentage / 100);
  } else if (subsidyConfig.includes('RD$')) {
    return parseFloat(subsidyConfig.replace('RD$', '').trim());
  }
  return 0;
}
function convertSupabaseOrder(supabaseOrder: any): Order {
  return {
    id: supabaseOrder.id,
    orderNo: supabaseOrder.order_no,
    orderDate: toRDTime(supabaseOrder.order_date),
    orderDateISO: supabaseOrder.order_date,
    deliveryDate: supabaseOrder.delivery_date ? toRDTime(supabaseOrder.delivery_date) : '',
    type: supabaseOrder.type,
    name: supabaseOrder.employee_name,
    company: supabaseOrder.company,
    branch: supabaseOrder.branch,
    product: supabaseOrder.product,
    cashier: 'Online',
    quantity: supabaseOrder.quantity,
    catalogType: supabaseOrder.catalog_type,
    subtotal: String(supabaseOrder.subtotal),
    discount: String(supabaseOrder.discount),
    subsidyAmount: String(supabaseOrder.subsidy_amount || '0'),
    subsidyAppliedBy: supabaseOrder.subsidy_applied_by,
    total: String(supabaseOrder.total),
    status: supabaseOrder.status,
    labelStatus: supabaseOrder.label_status,
    taxExempt: supabaseOrder.tax_exempt,
    employeeId: supabaseOrder.employee_id
  };
}
function convertToSupabaseOrder(order: any): any {
  return {
    id: order.id,
    order_no: order.orderNo,
    order_date: order.customOrderDate || new Date().toISOString(),
    delivery_date: null,
    type: order.type,
    employee_name: order.name,
    employee_id: order.employeeId || null,
    company: order.company,
    branch: order.branch,
    product: order.product,
    quantity: order.quantity,
    catalog_type: order.catalogType,
    subtotal: parseFloat(order.subtotal),
    discount: parseFloat(order.discount || '0'),
    subsidy_amount: parseFloat(order.subsidyAmount || '0'),
    subsidy_applied_by: order.subsidyAppliedBy || null,
    total: parseFloat(order.total),
    status: order.status,
    label_status: order.labelStatus,
    tax_exempt: order.taxExempt || false
  };
}
function getNextOrderNo(existingOrders: Order[]): string {
  let maxNo = 0;
  for (const order of existingOrders) {
    const num = parseInt(order.orderNo, 10);
    if (!isNaN(num) && num > maxNo && num < 1000000) {
      maxNo = num;
    }
  }
  return String(maxNo + 1);
}
function dispatchNewOrderEvent(order: any) {
  try {
    window.dispatchEvent(new CustomEvent('cricket-new-order', {
      detail: {
        orderNo: order.order_no || order.orderNo,
        name: order.employee_name || order.name,
        product: order.product,
        company: order.company
      }
    }));
  } catch (e) {
    // Silent fail
  }
}
export function OrderProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Stable loadOrders — wrapped in useCallback so it doesn't change on every render
  const loadOrders = useCallback(async () => {
    try {
      if (!isSupabaseConfigured()) {
        setError('Supabase no está configurado. Por favor configura las variables de entorno.');
        setLoading(false);
        return;
      }
      const supabaseOrders = await supabaseClient.getOrders();
      const convertedOrders = supabaseOrders.map(convertSupabaseOrder);
      setOrders(convertedOrders);
      setError(null);
    } catch (error: any) {
      console.error('[OrderContext] Error loading orders:', error.message);
      // Keep showing cached data on transient errors
    } finally {
      setLoading(false);
    }
  }, []); // No deps — intentionally stable
  // Load orders on mount
  useEffect(() => {
    loadOrders();
  }, [loadOrders]);
  // AUTO-FIX: Correct orders with "Sin empresa" or "Sin sucursal" in the database
  // Only runs once per day to avoid heavy DB queries on every mount
  useEffect(() => {
    const fixOrphanedOrders = async () => {
      if (!isSupabaseConfigured()) return;
      // Skip if already ran today
      const lastFixDate = localStorage.getItem('criket_orphan_fix_date');
      const today = new Date().toDateString();
      if (lastFixDate === today) return;
      try {
        const [companies, employees] = await Promise.all([supabaseClient.getCompanies(), supabaseClient.getEmployees()]);
        if (companies.length === 0 || employees.length === 0) return;
        localStorage.setItem('criket_companies', JSON.stringify(companies));
        const allOrders = await supabaseClient.getOrders();
        const ordersToFix = allOrders.filter((o: any) => o.company === 'Sin empresa' || o.company === 'N/A' || o.branch === 'Sin sucursal' || o.branch === 'N/A');
        // Mark as done for today even if no orders to fix
        localStorage.setItem('criket_orphan_fix_date', today);
        if (ordersToFix.length === 0) return;
        console.log(`Fixing ${ordersToFix.length} orders with missing company/branch...`);
        for (const order of ordersToFix) {
          const emp = employees.find((e: any) => e.id === order.employee_id);
          if (emp && emp.company_id) {
            const company = companies.find((c: any) => c.id === emp.company_id);
            const branch = company?.branches?.find((b: any) => b.id === emp.branch_id);
            if (company) {
              const updates: any = {};
              if (order.company === 'Sin empresa' || order.company === 'N/A') {
                updates.company = company.name;
              }
              if (order.branch === 'Sin sucursal' || order.branch === 'N/A') {
                updates.branch = branch?.name || 'Principal';
              }
              if (Object.keys(updates).length > 0) {
                try {
                  await supabaseClient.updateOrder(order.id, updates);
                } catch (e) {
                  console.error(`Error fixing order ${order.id}:`, e);
                }
              }
            }
          }
        }
        console.log('Orders fixed, reloading...');
        await loadOrders();
      } catch (e) {
        console.error('Error fixing orphaned orders:', e);
      }
    };
    const timer = setTimeout(fixOrphanedOrders, 3000);
    return () => clearTimeout(timer);
  }, [loadOrders]);
  // Real-time subscription — NO polling (real-time handles all updates)
  useEffect(() => {
    if (!isSupabaseConfigured()) return;
    let subscription: any = null;
    try {
      subscription = supabaseClient.subscribeToOrders(payload => {
        if (payload.eventType === 'INSERT') {
          const settings = JSON.parse(localStorage.getItem('criket_general_settings') || '{}');
          if (settings.enableNotifications !== false) {
            playNewOrderSound();
          }
          if (settings.autoAcceptOrders && payload.new?.id && payload.new?.status === 'Pendiente') {
            supabaseClient.updateOrder(payload.new.id, {
              status: 'En Preparación'
            }).catch(console.error);
          }
        }
        loadOrders();
      });
    } catch (error) {
      console.log('Real-time subscription failed, using polling fallback');
      // Polling only as fallback when real-time is unavailable
      const pollInterval = setInterval(async () => {
        try {
          const supabaseOrders = await supabaseClient.getOrders();
          const convertedOrders = supabaseOrders.map(convertSupabaseOrder);
          setOrders(convertedOrders);
          setError(null);
        } catch (e) {
          // Silent fail on polling
        }
      }, 30000);
      return () => clearInterval(pollInterval);
    }
    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [loadOrders]);
  // Stable action functions — wrapped in useCallback so they don't change
  // when orders state updates, preventing unnecessary re-renders in consumers
  const addOrder = useCallback(async (newOrderData: Omit<Order, 'id' | 'orderNo' | 'orderDate' | 'status' | 'labelStatus'>): Promise<string> => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase no está configurado');
    }
    const companies = getCompanies();
    const company = companies.find(c => c.name === newOrderData.company);
    let initialStatus: Order['status'] = 'Pendiente';
    try {
      const dbSettings = await supabaseClient.getSettings('general');
      if (dbSettings && dbSettings.autoAcceptOrders) {
        initialStatus = 'En Preparación';
      } else {
        const savedSettings = localStorage.getItem('criket_general_settings');
        if (savedSettings) {
          const localSettings = JSON.parse(savedSettings);
          if (localSettings.autoAcceptOrders) {
            initialStatus = 'En Preparación';
          }
        }
      }
    } catch (e) {
      try {
        const savedSettings = localStorage.getItem('criket_general_settings');
        if (savedSettings) {
          const localSettings = JSON.parse(savedSettings);
          if (localSettings.autoAcceptOrders) {
            initialStatus = 'En Preparación';
          }
        }
      } catch (e2) {}
    }
    const orderSubtotal = parseFloat(newOrderData.subtotal);
    const isTaxExempt = newOrderData.taxExempt || false;
    let subsidyAmount = '0';
    let subsidyAppliedBy = undefined;
    let finalTotal = 0;
    if (company && company.subsidy && company.subsidy !== '0' && company.subsidy !== 'RD$ 0') {
      const calculatedSubsidy = calculateSubsidyAmount(orderSubtotal, company.subsidy);
      if (calculatedSubsidy > 0) {
        subsidyAmount = calculatedSubsidy.toFixed(2);
        subsidyAppliedBy = 'Sistema (Automático)';
      }
    }
    const subtotalAfterSubsidy = orderSubtotal - parseFloat(subsidyAmount);
    finalTotal = subtotalAfterSubsidy;
    let latestOrders: Order[] = [];
    try {
      const freshOrders = await supabaseClient.getOrders();
      latestOrders = freshOrders.map(convertSupabaseOrder);
    } catch (e) {
      // Use empty fallback
    }
    const nextOrderNo = getNextOrderNo(latestOrders);
    const newOrder: Order = {
      ...newOrderData,
      id: generateSecureId('order'),
      orderNo: nextOrderNo,
      orderDate: toRDTime(new Date()),
      status: initialStatus,
      labelStatus: 'Pendiente',
      subsidyAmount,
      subsidyAppliedBy,
      total: finalTotal.toFixed(2)
    };
    try {
      const supabaseOrder = convertToSupabaseOrder(newOrder);
      await supabaseClient.createOrder(supabaseOrder);
      await loadOrders();
      return nextOrderNo;
    } catch (error: any) {
      const message = error.message || 'Error desconocido';
      if (message.includes('520') || message.includes('502') || message.includes('503') || message.includes('504')) {
        throw new Error('El servidor está temporalmente no disponible. Por favor intenta de nuevo en unos segundos.');
      }
      throw new Error('Error al guardar el pedido: ' + message);
    }
  }, [loadOrders]);
  const updateOrderStatus = useCallback(async (orderId: string, status: Order['status']) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase no está configurado');
    }
    try {
      await supabaseClient.updateOrder(orderId, {
        status
      });
      await loadOrders();
    } catch (error: any) {
      throw new Error('Error al actualizar el pedido: ' + error.message);
    }
  }, [loadOrders]);
  const updateLabelStatus = useCallback(async (orderIds: string[], status: 'Generado') => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase no está configurado');
    }
    try {
      for (const orderId of orderIds) {
        await supabaseClient.updateOrder(orderId, {
          label_status: status
        });
      }
      await loadOrders();
    } catch (error: any) {
      throw new Error('Error al actualizar etiquetas: ' + error.message);
    }
  }, [loadOrders]);
  const cancelOrder = useCallback(async (orderId: string) => {
    const order = orders.find(o => o.id === orderId);
    await updateOrderStatus(orderId, 'Cancelado');
    if (order?.employeeId && order.total) {
      try {
        const amount = parseFloat(order.total);
        if (amount > 0) {
          const orderDate = order.orderDateISO ? new Date(order.orderDateISO) : new Date();
          const month = orderDate.getMonth() + 1;
          const year = orderDate.getFullYear();
          await supabaseClient.refundBalance(order.employeeId, month, year, amount, 'Admin', `Reembolso automático por cancelación de orden #${order.orderNo}`);
        }
      } catch (e) {
        console.error('[OrderContext] Balance refund on cancel failed (non-critical):', e);
      }
    }
  }, [orders, updateOrderStatus]);
  const applySubsidy = useCallback(async (orderId: string, subsidyAmount: string, appliedBy: string) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase no está configurado');
    }
    const order = orders.find(o => o.id === orderId);
    if (!order) throw new Error('Order not found');
    const newTotal = (parseFloat(order.total) - parseFloat(subsidyAmount)).toFixed(2);
    try {
      await supabaseClient.updateOrder(orderId, {
        subsidy_amount: parseFloat(subsidyAmount),
        subsidy_applied_by: appliedBy,
        total: parseFloat(newTotal)
      });
      await loadOrders();
    } catch (error: any) {
      throw new Error('Error al aplicar subsidio: ' + error.message);
    }
  }, [orders, loadOrders]);
  const filteredOrders = useCallback((searchTerm: string, company: string, status?: string) => {
    return orders.filter(order => {
      const matchesSearch = order.name.toLowerCase().includes(searchTerm.toLowerCase()) || order.orderNo.includes(searchTerm) || order.product.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCompany = company === 'Todas' || order.company === company;
      const matchesStatus = !status || status === 'Todos' || order.status === status;
      return matchesSearch && matchesCompany && matchesStatus;
    });
  }, [orders]);
  // Memoize context value — consumers that only use stable functions (e.g. addOrder)
  // won't re-render when the orders array changes
  const contextValue = useMemo(() => ({
    orders,
    loading,
    refreshOrders: loadOrders,
    addOrder,
    updateOrderStatus,
    updateLabelStatus,
    cancelOrder,
    applySubsidy,
    filteredOrders
  }), [orders, loading, loadOrders, addOrder, updateOrderStatus, updateLabelStatus, cancelOrder, applySubsidy, filteredOrders]);
  // Actions context — only changes when action functions change (very rare)
  // This prevents EmployeeOrderPage from re-rendering on every order update
  const actionsValue = useMemo(() => ({
    addOrder,
    updateOrderStatus,
    updateLabelStatus,
    cancelOrder,
    applySubsidy,
    refreshOrders: loadOrders
  }), [addOrder, updateOrderStatus, updateLabelStatus, cancelOrder, applySubsidy, loadOrders]);
  // Show error message if Supabase is not configured
  if (error && !isSupabaseConfigured()) {
    return <OrderContext.Provider value={{
      orders: [],
      loading: false,
      refreshOrders: async () => {},
      addOrder: async () => {
        throw new Error(error);
      },
      updateOrderStatus: async () => {
        throw new Error(error);
      },
      updateLabelStatus: async () => {
        throw new Error(error);
      },
      cancelOrder: async () => {
        throw new Error(error);
      },
      applySubsidy: async () => {
        throw new Error(error);
      },
      filteredOrders: () => []
    }} data-id="element-1032">
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4" data-id="element-1033">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-red-200 dark:border-red-800" data-id="element-1034">
            <div className="text-center" data-id="element-1035">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1036">
                <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1037">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" data-id="element-1038" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-1039">
                Base de datos no configurada
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-4" data-id="element-1040">{error}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1041">
                Por favor, configura las variables de entorno VITE_SUPABASE_URL
                y VITE_SUPABASE_ANON_KEY en tu servidor de deployment.
              </p>
            </div>
          </div>
        </div>
      </OrderContext.Provider>;
  }
  return <OrderContext.Provider value={contextValue} data-id="element-1042">
      <OrderActionsContext.Provider value={actionsValue} data-id="element-1043">
        {children}
      </OrderActionsContext.Provider>
    </OrderContext.Provider>;
}
export function useOrders() {
  const context = useContext(OrderContext);
  if (context === undefined) {
    throw new Error('useOrders must be used within an OrderProvider');
  }
  return context;
}
// Lightweight hook for components that only need to CREATE orders (no re-renders on order list changes)
export function useOrderActions() {
  const context = useContext(OrderActionsContext);
  if (context === undefined) {
    throw new Error('useOrderActions must be used within an OrderProvider');
  }
  return context;
}